import React, { useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../ui/UIElements/Icon";
import Modal from "../../ui/UIElements/Modal";
import { toast } from "sonner";
import { useToggleUserStatus } from "../../../hooks/useToggleUserStatus";
import { useUserDetail } from "../../../hooks/useUserDetail";
import { useUsers } from "../../../hooks/useUsers";
import { useGlobalConfigs } from "@finitless/shared/hooks";
import Breadcrumb from "../../ui/Navigation/Breadcrumb";
import WalletsTab from "../../ui/UserDetailsModal/WalletsTab";
import LoyaltyPointsTab from "../../ui/UserDetailsModal/LoyaltyPointsTab";
import OverviewTab from "../../ui/UserDetailsModal/OverviewTab";
import OrderHistoryTab from "../../ui/UserDetailsModal/OrderHistoryTab";
import AddressesTab from "../../ui/UserDetailsModal/AddressesTab";
import {
  getUserName,
  getUserInitials,
  getUserOrdersCount,
  getUserStatusBadge,
  getUserStatusBadgeColor,
  USER_STATUS_COLORS,
  getVisibleTabsForUserLevel,
  isTabVisibleForUserLevel,
  USER_LEVELS,
} from "../../../constants/userConstants";

const CustomerDetailPage = ({
  initialUser = null,
  basePath = "/users",
  baseLabel = null,
  idLabelKey = "detail.labels.customerId",
}) => {
  const { t } = useTranslation(["customers", "common"]);
  const { id } = useParams();
  const userId = parseInt(id);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  // Toggle user status hook
  const toggleUserStatusMutation = useToggleUserStatus();

  // Get global configs for wallet feature check
  const { data: configs } = useGlobalConfigs();

  // Get user data from navigation state or props
  const userFromState = location.state?.user;
  const userData = initialUser || userFromState;

  // Fetch user data using custom hook
  const {
    user: currentUser,
    isLoading,
    error,
    isError,
    updateUser,
    refetch: refetchUser,
    isFetching,
  } = useUserDetail(userId, userData);

  // Get visible tabs based on user level and wallet config
  const visibleTabs = useMemo(() => {
    return getVisibleTabsForUserLevel(currentUser?.level, configs);
  }, [currentUser?.level, configs]);

  // Fetch users for transaction history author lookup - only if wallets tab is visible
  const shouldFetchUsers = currentUser
    ? isTabVisibleForUserLevel("wallets", currentUser.level, configs)
    : false;
  const { data: usersData } = useUsers({
    page_size: 1000,
    enabled: shouldFetchUsers,
  }); // Get all users for author lookup only if needed

  const handleToggleUserStatus = () => {
    if (currentUser.enabled) {
      setShowDeactivateModal(true);
    } else {
      handleConfirmToggle();
    }
  };

  const handleConfirmToggle = async () => {
    try {
      const newEnabledStatus = !currentUser.enabled;

      await toggleUserStatusMutation.mutateAsync({
        userId: currentUser.id,
        enabled: newEnabledStatus,
      });

      updateUser((prev) => ({
        ...prev,
        enabled: newEnabledStatus,
      }));

      toast.success("Status Updated", {
        description: t("detail.toasts.statusUpdated", {
          status: newEnabledStatus
            ? t("detail.states.activated")
            : t("detail.states.deactivated"),
        }),
        duration: 3000,
      });

      setShowDeactivateModal(false);
    } catch {
      toast.error(t("toasts.updateFailedTitle"), {
        description: t("detail.toasts.statusUpdateFailed"),
        duration: 3000,
      });
    }
  };

  const handleBackToCustomers = () => {
    navigate(basePath);
  };

  const handleRefreshUser = async () => {
    try {
      await refetchUser();
      toast.success(t("detail.toasts.userRefreshedTitle"), {
        description: t("detail.toasts.userRefreshedDescription"),
        duration: 3000,
      });
    } catch {
      toast.error(t("detail.toasts.refreshFailedTitle"), {
        description: t("detail.toasts.refreshFailedDescription"),
        duration: 3000,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-96 items-center justify-center p-4">
        <div className="text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500 sm:mb-4 sm:h-12 sm:w-12"></div>
          <p className="text-sm text-gray-400 sm:text-base">
            {t("detail.states.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (isError || (!isLoading && !currentUser)) {
    return (
      <div className="px-4 py-8 text-center sm:py-12">
        <Icon
          name="UserX"
          className="mx-auto mb-3 h-8 w-8 text-gray-400 sm:mb-4 sm:h-12 sm:w-12"
        />
        <h3 className="mt-2 text-sm font-medium text-gray-300 sm:text-base">
          {t("detail.states.notFoundTitle")}
        </h3>
        <p className="mt-1 text-xs text-gray-400 sm:text-sm">
          {error?.message || t("detail.states.notFoundDescription")}
        </p>
        <button
          onClick={handleBackToCustomers}
          className="mt-3 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white transition-colors hover:bg-blue-700 sm:mt-4 sm:px-4 sm:text-base"
        >
          {basePath === "/users"
            ? t("detail.actions.backToCustomers")
            : t("adminsPage.detail.actions.backToAdmins")}
        </button>
      </div>
    );
  }

  const name = getUserName(currentUser);
  const initials = getUserInitials(currentUser);
  const ordersCount = getUserOrdersCount(currentUser);
  const statusBadge = getUserStatusBadge(currentUser.enabled);
  const statusBadgeColor = getUserStatusBadgeColor(currentUser.enabled);

  // Only show tabs if there's more than one (overview + others)
  const shouldShowTabs = visibleTabs.length > 1;

  const breadcrumbItems = [
    {
      label: baseLabel || t("header.title"),
      path: basePath,
      icon: basePath === "/users" ? "Users" : "Shield",
    },
    {
      label: currentUser
        ? getUserName(currentUser)
        : t("states.loading", { ns: "common" }),
      active: true,
      disabled: true,
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* User Profile Header */}
      <div className="rounded-lg border border-gray-800 bg-gray-900">
        <div className="border-b border-gray-800 p-3 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full sm:h-16 sm:w-16">
                {currentUser.photo ? (
                  <img
                    src={currentUser.photo}
                    alt={name}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className={`flex h-full w-full items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 text-lg font-semibold text-white sm:text-2xl ${
                    currentUser.photo ? "hidden" : "flex"
                  }`}
                >
                  {initials}
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="truncate text-lg font-bold text-white sm:text-2xl">
                  {name}
                </h1>
                <p className="text-xs text-gray-400 sm:text-sm">
                  {t(idLabelKey, { id: currentUser.id })}
                </p>
              </div>
            </div>

            {/* Status and Actions */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-400 sm:text-sm">
                  {t("detail.labels.status")}:
                </span>
                <span
                  className={`rounded-full px-2 py-1 text-xs font-medium sm:px-3 sm:text-sm ${statusBadgeColor}`}
                >
                  {statusBadge}
                </span>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                <button
                  onClick={handleRefreshUser}
                  disabled={isFetching}
                  className="flex cursor-pointer items-center justify-center space-x-2 rounded-lg bg-gray-700 px-3 py-2 text-xs text-white transition-colors hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:text-sm"
                >
                  {isFetching ? (
                    <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-white sm:h-4 sm:w-4"></div>
                  ) : (
                    <Icon name="RefreshCw" className="h-3 w-3 sm:h-4 sm:w-4" />
                  )}
                  <span className="sm:inline">
                    {isFetching
                      ? t("detail.actions.refreshing")
                      : t("detail.actions.refresh")}
                  </span>
                </button>
                <button
                  onClick={handleToggleUserStatus}
                  disabled={toggleUserStatusMutation.isPending}
                  className={`flex cursor-pointer items-center justify-center space-x-2 rounded-lg px-3 py-2 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:text-sm ${
                    currentUser.enabled
                      ? USER_STATUS_COLORS.INACTIVE.button + " text-white"
                      : USER_STATUS_COLORS.ACTIVE.button + " text-white"
                  }`}
                >
                  <Icon
                    name={currentUser.enabled ? "UserX" : "UserCheck"}
                    className="h-3 w-3 sm:h-4 sm:w-4"
                  />
                  <span className="hidden sm:inline">
                    {toggleUserStatusMutation.isPending
                      ? t("detail.actions.updating")
                      : currentUser.enabled
                        ? t("detail.actions.deactivateUser")
                        : t("detail.actions.activateUser")}
                  </span>
                  <span className="sm:hidden">
                    {toggleUserStatusMutation.isPending
                      ? t("detail.actions.updating")
                      : currentUser.enabled
                        ? t("detail.actions.deactivate")
                        : t("detail.actions.activate")}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs - Only show if there are multiple tabs */}
        {shouldShowTabs && (
          <div className="border-b border-gray-800 px-3 sm:px-6">
            <nav className="flex space-x-4 overflow-x-auto sm:space-x-8">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`border-b-2 px-1 py-3 text-xs font-medium whitespace-nowrap transition-colors sm:py-4 sm:text-sm ${
                    activeTab === tab.id
                      ? "border-purple-500 text-white"
                      : "border-transparent text-gray-400 hover:border-gray-300 hover:text-gray-300"
                  }`}
                >
                  {tab.labelKey ? t(tab.labelKey) : tab.label}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Main Content */}
        <div className="p-3 sm:p-6">
          {/* Overview Tab - Always visible */}
          <div className={activeTab === "overview" ? "block" : "hidden"}>
            <OverviewTab
              user={currentUser}
              ordersCount={ordersCount}
              showStats={currentUser.level !== USER_LEVELS.ADMIN}
            />
          </div>

          {/* Order History Tab - Only for customers and business owners */}
          {isTabVisibleForUserLevel(
            "order-history",
            currentUser.level,
            configs,
          ) && (
            <div className={activeTab === "order-history" ? "block" : "hidden"}>
              <OrderHistoryTab userId={currentUser?.id} />
            </div>
          )}

          {/* Wallets Tab - Only for customers and business owners, and if wallet is enabled */}
          {isTabVisibleForUserLevel("wallets", currentUser.level, configs) && (
            <div className={activeTab === "wallets" ? "block" : "hidden"}>
              <WalletsTab
                wallets={currentUser?.wallets}
                userId={currentUser?.id}
                users={usersData?.users || []}
              />
            </div>
          )}

          {/* Loyalty Points Tab - Only for customers and business owners */}
          {isTabVisibleForUserLevel(
            "loyalty-points",
            currentUser.level,
            configs,
          ) && (
            <div
              className={activeTab === "loyalty-points" ? "block" : "hidden"}
            >
              <LoyaltyPointsTab loyaltyLevel={currentUser?.loyalty_level} />
            </div>
          )}

          {/* Addresses Tab */}
          {isTabVisibleForUserLevel(
            "addresses",
            currentUser.level,
            configs,
          ) && (
            <div className={activeTab === "addresses" ? "block" : "hidden"}>
              <AddressesTab
                addresses={currentUser?.addresses}
                userId={currentUser?.id}
                onUpdate={() => refetchUser()}
              />
            </div>
          )}
        </div>
      </div>

      {/* Deactivate User Confirmation Modal */}
      <Modal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        title={t("detail.deactivateModal.title")}
        subtitle={t("detail.deactivateModal.subtitle")}
        maxWidth="max-w-md"
      >
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-start space-x-2 sm:space-x-3">
            <div className="flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500/20 sm:h-10 sm:w-10">
                <Icon
                  name="AlertTriangle"
                  className="h-4 w-4 text-red-400 sm:h-5 sm:w-5"
                />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-medium text-white">
                {t("detail.deactivateModal.confirmName", { name })}
              </h3>
              <p className="mt-1 text-xs text-gray-400 sm:text-sm">
                {t("detail.deactivateModal.description")}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-3 sm:flex-row sm:gap-3 sm:pt-4">
            <button
              onClick={handleConfirmToggle}
              disabled={toggleUserStatusMutation.isPending}
              className="flex flex-1 items-center justify-center space-x-2 rounded-lg bg-red-600 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:text-sm"
            >
              {toggleUserStatusMutation.isPending ? (
                <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-white sm:h-4 sm:w-4"></div>
              ) : null}
              <span>
                {toggleUserStatusMutation.isPending
                  ? t("detail.actions.deactivating")
                  : t("detail.actions.deactivateUser")}
              </span>
            </button>
            <button
              onClick={() => setShowDeactivateModal(false)}
              disabled={toggleUserStatusMutation.isPending}
              className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-xs font-medium text-gray-300 transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:text-sm"
            >
              {t("actions.cancel", { ns: "common" })}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CustomerDetailPage;
